import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FileText, Calendar, ChevronRight, Save, Send, Lock, Info, RotateCcw, Download, Archive } from "lucide-react";
import { RelationRenderer } from "@/components/office/RelationRenderer";
import { slugFromKey } from "@/lib/services";

function fmtDate(s) {
  if (!s) return "—";
  const d = new Date(s.length <= 10 ? `${s}T00:00:00` : s);
  return isNaN(d) ? s : d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
}

const STATUS_META = {
  da_compilare: { label: "Da compilare", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  in_bozza: { label: "In bozza", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  consegnata: { label: "Consegnata", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  revisione_richiesta: { label: "Revisione richiesta", cls: "bg-orange-50 text-orange-700 border-orange-200" },
  approvata: { label: "Approvata", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
};

function StatusBadge({ status, expired }) {
  const m = STATUS_META[status] || STATUS_META.da_compilare;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${m.cls}`}>{m.label}</span>
      {expired && status !== "consegnata" && (
        <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-600">Scaduta</span>
      )}
    </span>
  );
}

// Validazione lato client (allineata al backend) per evidenziare i campi mancanti alla consegna.
function answerFilled(f, v) {
  if (f.type === "section" || f.type === "info") return true;
  if (v === undefined || v === null) return false;
  if (f.type === "multi_choice") return Array.isArray(v) && v.length > 0;
  if (f.type === "semi_open") {
    if (typeof v !== "object") return false;
    const c = (v.choice || "").trim();
    if (!c) return false;
    if (c === "__other__") return !!(v.other || "").trim();
    return true;
  }
  return String(v).trim() !== "";
}

function RelationDialog({ instanceId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [invalid, setInvalid] = useState([]);
  const [confirmSubmit, setConfirmSubmit] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/worker/relations/instances/${instanceId}`);
      setData(r.data); setAnswers(r.data.answers || {});
    } catch (e) { toast.error(formatApiError(e)); onClose(); }
  }, [instanceId, onClose]);
  useEffect(() => { load(); }, [load]);

  const onChange = (fid, v) => setAnswers((a) => ({ ...a, [fid]: v }));

  const saveDraft = async () => {
    setBusy(true);
    try {
      await api.put(`/worker/relations/instances/${instanceId}/answers`, { answers });
      toast.success("Bozza salvata"); onChanged && onChanged(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const trySubmit = () => {
    const miss = (data.fields || []).filter((f) => f.required && !answerFilled(f, answers[f.field_id])).map((f) => f.field_id);
    setInvalid(miss);
    if (miss.length > 0) { toast.error(`Compila i ${miss.length} campi obbligatori mancanti`); return; }
    setConfirmSubmit(true);
  };
  const doSubmit = async () => {
    setBusy(true);
    try {
      await api.post(`/worker/relations/instances/${instanceId}/submit`, { answers });
      toast.success("Relazione consegnata"); setConfirmSubmit(false); onChanged && onChanged(); onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && Array.isArray(d.missing)) { setInvalid(d.missing); toast.error(d.message || "Campi obbligatori mancanti"); }
      else toast.error(formatApiError(e));
      setConfirmSubmit(false);
    } finally { setBusy(false); }
  };

  const editable = data?.can_edit;

  return (
    <>
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="worker-relation-dialog">
          <DialogHeader><DialogTitle>{data?.title || "Relazione"}</DialogTitle></DialogHeader>
          {!data ? (
            <p className="text-sm text-slate-500 p-4">Caricamento…</p>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> Scadenza: {fmtDate(data.due_date)}</span>
                <StatusBadge status={data.status} expired={data.is_expired} />
              </div>
              {data.status === "revisione_richiesta" && data.revision_note && (
                <div className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-2.5" data-testid="worker-revision-box">
                  <p className="text-sm font-semibold text-orange-800 flex items-center gap-1.5"><RotateCcw className="h-4 w-4" /> Revisione richiesta</p>
                  <p className="text-sm text-orange-700 mt-1 whitespace-pre-wrap">{data.revision_note}</p>
                  <p className="text-xs text-orange-600 mt-1">Nuova scadenza: {fmtDate(data.due_date)}</p>
                </div>
              )}
              {!editable && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 flex items-start gap-2" data-testid="worker-relation-readonly-note">
                  {data.status === "consegnata" ? <Lock className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" /> : <Info className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />}
                  <div className="text-xs text-slate-600">
                    {data.status === "consegnata"
                      ? "Relazione consegnata. Non è più modificabile."
                      : "La compilazione di questa relazione è riservata all'operatore titolare dell'utente."}
                    {data.assigned_worker_name && <div className="mt-0.5">Operatore titolare: <span className="font-semibold text-slate-700">{data.assigned_worker_name}</span></div>}
                  </div>
                </div>
              )}
              <RelationRenderer title={null} description={data.description} fields={data.fields || []}
                values={answers} onChange={onChange} readOnly={!editable} invalidIds={invalid} />
            </div>
          )}
          {editable && (
            <DialogFooter>
              <Button variant="outline" onClick={saveDraft} disabled={busy} data-testid="worker-relation-save"><Save className="h-4 w-4 mr-1.5" /> Salva bozza</Button>
              <Button onClick={trySubmit} disabled={busy} className="bg-[#22364D] hover:bg-[#2f4a6b]" data-testid="worker-relation-submit"><Send className="h-4 w-4 mr-1.5" /> Consegna relazione</Button>
            </DialogFooter>
          )}
          {!editable && data && data.status === "approvata" && (
            <DialogFooter>
              <Button variant="outline" onClick={() => downloadRelationPdf(instanceId, "worker")} data-testid="worker-relation-pdf-dialog"><Download className="h-4 w-4 mr-1.5" /> Scarica PDF</Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmSubmit} onOpenChange={(o) => !o && setConfirmSubmit(false)}>
        <AlertDialogContent data-testid="worker-relation-submit-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Confermi la consegna?</AlertDialogTitle>
            <AlertDialogDescription>Confermi la consegna della relazione? Dopo la consegna non potrai modificarla finché l'Ufficio non richiederà una revisione.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doSubmit} className="bg-[#22364D] hover:bg-[#2f4a6b]" data-testid="worker-relation-submit-final">Consegna</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

async function downloadRelationPdf(instanceId, scope = "worker") {
  try {
    const res = await api.get(`/${scope}/relations/instances/${instanceId}/pdf`, { responseType: "blob" });
    const cd = res.headers["content-disposition"] || "";
    const m = cd.match(/filename="?([^"]+)"?/);
    const name = m ? m[1] : "relazione.pdf";
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = name; document.body.appendChild(a); a.click();
    a.remove(); window.URL.revokeObjectURL(url);
  } catch (e) { toast.error("Impossibile generare il PDF"); }
}

export function WorkerRelationsSection({ userId }) {
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    if (!userId) return;
    try { const r = await api.get(`/worker/relations/by-user?user_id=${userId}`); setItems(r.data || []); }
    catch { setItems([]); }
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  if (!items.length) return null;
  const inCorso = items.filter((it) => it.status !== "approvata");
  const archivio = items.filter((it) => it.status === "approvata");

  const Card2 = ({ it, archived }) => (
    <Card key={it.instance_id} className="rounded-xl border-slate-200 shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <button onClick={() => setOpenId(it.instance_id)} className="min-w-0 flex-1 text-left" data-testid={`worker-relation-item-${it.instance_id}`}>
            <p className="font-semibold text-slate-800 truncate">{it.title}</p>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
              {it.service_label && <span>{it.service_label}</span>}
              {it.period_ref && <span>{it.period_ref}</span>}
              {archived ? (
                <>
                  {it.submitted_by_name && <span>Compilata da {it.submitted_by_name}</span>}
                  {it.submitted_at && <span>Consegnata {fmtDate(it.submitted_at)}</span>}
                  {it.approved_at && <span>Approvata {fmtDate(it.approved_at)}</span>}
                  {it.version_number ? <span>v{it.version_number}</span> : null}
                </>
              ) : (
                <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> Scad. {fmtDate(it.due_date)}</span>
              )}
            </div>
            <div className="mt-1.5"><StatusBadge status={it.status} expired={it.is_expired} /></div>
          </button>
          {archived && (
            <Button size="sm" variant="outline" onClick={() => downloadRelationPdf(it.instance_id, "worker")} data-testid={`worker-relation-pdf-${it.instance_id}`}>
              <Download className="h-3.5 w-3.5 mr-1" /> PDF
            </Button>
          )}
          <ChevronRight className="h-5 w-5 text-slate-400 shrink-0" />
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="mt-2" data-testid="worker-relations-section">
      {inCorso.length > 0 && (
        <div className="mb-5">
          <h2 className="font-heading text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#22364D]" /> Da completare / In corso
          </h2>
          <div className="space-y-2.5">{inCorso.map((it) => <Card2 key={it.instance_id} it={it} archived={false} />)}</div>
        </div>
      )}
      <div>
        <h2 className="font-heading text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <Archive className="h-5 w-5 text-[#22364D]" /> Archivio relazioni
        </h2>
        {archivio.length === 0 ? (
          <p className="text-sm text-slate-400 py-2" data-testid="worker-archive-empty">Nessuna relazione approvata.</p>
        ) : (
          <div className="space-y-2.5">{archivio.map((it) => <Card2 key={it.instance_id} it={it} archived={true} />)}</div>
        )}
      </div>
      {openId && <RelationDialog instanceId={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}

export default WorkerRelationsSection;

// Richiamo leggero per la Home Lavoratore: relazioni assegnate e non ancora consegnate.
export function WorkerRelationsCallout() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/worker/relations/pending").then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, []);
  if (!items.length) return null;
  return (
    <div className="mt-4 rounded-xl border border-[#C9D8E6] bg-[#EDF5FA] px-4 py-3" data-testid="worker-relations-callout">
      <div className="flex items-center gap-2 mb-2">
        <FileText className="h-4 w-4 text-[#22364D]" />
        <p className="text-sm font-semibold text-slate-800">Relazioni da compilare</p>
        <span className="ml-auto text-xs font-bold text-white bg-amber-500 rounded-full px-2 py-0.5" data-testid="worker-relations-callout-badge">{items.length}</span>
      </div>
      <div className="space-y-1.5">
        {items.slice(0, 4).map((it) => {
          const isRev = it.status === "revisione_richiesta";
          return (
          <a key={it.instance_id} href={`/registro/${slugFromKey(it.service)}/${it.user_id}`}
            className={`flex items-center gap-2 rounded-lg border px-3 py-2 transition-colors ${isRev ? "bg-orange-50 border-orange-300 hover:border-orange-400" : "bg-white border-slate-200 hover:border-[#93B4CC]"}`}
            data-testid={`worker-relation-pending-${it.instance_id}`}>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-slate-800 truncate">{it.title}
                {isRev && <span className="ml-2 inline-flex items-center rounded-full bg-orange-100 border border-orange-300 px-1.5 py-0.5 text-[10px] font-bold text-orange-700 align-middle">REVISIONE</span>}
              </p>
              <p className="text-xs text-slate-500 truncate">{it.user_name} · Scad. {fmtDate(it.due_date)}</p>
            </div>
            {it.is_expired && <span className="text-xs font-semibold text-red-600 shrink-0">Scaduta</span>}
            <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />
          </a>
          );
        })}
      </div>
    </div>
  );
}
